/* eslint-disable no-useless-escape */
import React, { useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
// import { useIntl } from 'react-intl';

import Dropdown from '~components/dropdown';

import { COUNTRY_CODES, DEFAULT_COUNTRY_CODE } from '~src/constants';
import './style.scss';

const LIST_ITEM_HEIGHT = 50;

const CountryOption = ({ country, onClick, className, forwardRef }) => {
  return (
    <button
      type="button"
      onClick={() => onClick(country.isoCode)}
      className={cx('c-country-code__option', {
        [className]: !!className,
      })}
      ref={forwardRef}
      role="option"
      aria-label={`${country.name} ${country.dialCode}`}
    >
      <img alt="" src={`/images/flags/${country.isoCode}.svg`} aria-hidden="true" />
      <div className="c-country-code__name">{country.name}</div>
      <span>{country.dialCode}</span>
    </button>
  );
};

const CountryCodeInput = ({
  value = DEFAULT_COUNTRY_CODE,
  onChange = () => {},
  className,
  placement = 'bottom',
  initialVisible = false,
}) => {
  // const intl = useIntl();

  const CountriesList = [...COUNTRY_CODES];

  // Remove translations for
  // const CountriesList = COUNTRY_CODES.map((c) => ({
  //   dialCode: c.dialCode,
  //   isoCode: c.isoCode,
  //   translatedName: intl.formatMessage({ id: `countries.${c.isoCode}` }),
  // }));
  // CountriesList.sort((a, b) => (a.translatedName > b.translatedName ? 1 : b.translatedName > a.translatedName ? -1 : 0));

  const currentCountry = value ? CountriesList.find((c) => c.isoCode === value) : null;
  const [visible, setVisible] = useState(initialVisible);
  const [cursor, setCursor] = useState(-1);
  const [popperPlacement, setPopperPlacement] = useState(placement);

  const optionsWrapperRef = useRef();
  const refs = CountriesList.reduce((acc, v) => {
    acc[CountriesList.indexOf(v)] = React.createRef();
    return acc;
  }, {});

  const handleChange = (v) => {
    if (!v) {
      return;
    }
    onChange(v);
    setVisible(false);
  };

  const downHandler = (ev) => {
    if (!ev.key || !visible) {
      return;
    }

    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      setCursor((prevState) => (prevState < CountriesList.length - 1 ? prevState + 1 : prevState));
      return;
    }

    if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setCursor((prevState) => (prevState >= 0 ? prevState - 1 : prevState));
      return;
    }

    if (ev.key === 'Enter') {
      ev.preventDefault();
      if (cursor === -1) {
        handleChange(currentCountry.isoCode);
        return;
      }
      handleChange(CountriesList[cursor].isoCode);
      return;
    }

    if (/^[A-Za-z]+$/.test(ev.key)) {
      ev.preventDefault();
      const toSearchArray = [...CountriesList.slice(cursor + 1), ...CountriesList.slice(0, cursor >= 0 ? cursor : 0)];
      const nextFoundItem = toSearchArray.find((c) => c.name.charAt(0).toLowerCase() === ev.key.toLowerCase());
      if (nextFoundItem) {
        setCursor(CountriesList.indexOf(nextFoundItem));
      }
    }
  };

  const upHandler = (ev) => {
    if (!visible) {
      return;
    }
    ev.preventDefault();
  };

  useEffect(() => {
    window.addEventListener('keydown', downHandler);
    window.addEventListener('keyup', upHandler);

    return () => {
      window.removeEventListener('keydown', downHandler);
      window.removeEventListener('keyup', upHandler);
    };
  }, [visible, cursor]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    if (!optionsWrapperRef.current || !optionsWrapperRef.current.scrollTo) {
      return;
    }

    if (cursor === -1) {
      optionsWrapperRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    optionsWrapperRef.current.scrollTo({
      top: refs[cursor].current.offsetTop - optionsWrapperRef.current.offsetHeight + LIST_ITEM_HEIGHT,
      behavior: 'smooth',
    });
  }, [cursor, visible]);

  return (
    <Dropdown.Container
      className={cx('c-country-code', `c-country-code--placement-${popperPlacement}`, {
        'c-country-code--opened': visible,
        [className]: !!className,
      })}
      placement="bottom-start"
      visible={visible}
      onVisibleChange={setVisible}
      optionsClassName={cx('c-country-code__options')}
      optionsWrapperRef={optionsWrapperRef}
      menuRole="listbox"
      toggleLabel={`Select country code${currentCountry ? `. Current: ${currentCountry.name} ${currentCountry.dialCode}` : ''}`}
      menuLabel="Country codes"
      onPopperMove={(popper) => {
        setPopperPlacement(popper && popper['data-popper-placement'] ? popper['data-popper-placement'] : placement);
      }}
    >
      <Dropdown.Toggle>
        <div className="c-country-code__toggle">
          {visible ? (
            <FiChevronUp className="c-country-code__chevron c-country-code__chevron--up" aria-hidden="true" />
          ) : (
            <FiChevronDown className="c-country-code__chevron c-country-code__chevron--down" aria-hidden="true" />
          )}
          {value && currentCountry && currentCountry.dialCode ? <div>{currentCountry.dialCode}</div> : ''}
        </div>
      </Dropdown.Toggle>
      <Dropdown.Content>
        {currentCountry ? (
          <CountryOption
            key="currentCountry"
            country={currentCountry}
            onClick={() => handleChange(currentCountry.isoCode)}
            className={cx('c-country-code__option--selected', {
              'c-country-code__option--hovered': cursor === -1,
            })}
          />
        ) : null}
        {CountriesList.map((c) => {
          return (
            <CountryOption
              key={c.isoCode}
              country={c}
              onClick={() => handleChange(c.isoCode)}
              item={c}
              forwardRef={refs[CountriesList.indexOf(c)]}
              className={cx({
                'c-country-code__option--hovered': cursor === CountriesList.indexOf(c),
              })}
            />
          );
        })}
      </Dropdown.Content>
    </Dropdown.Container>
  );
};

export default CountryCodeInput;
